import { SelectDatePopUp } from "@pages/Routines/Routine/RoutineActions/SelectDatePopUp";
import { RoutineDuplicationPopup } from "@src/components/deprecated/RoutineDuplicationPopup/RoutineDuplicationPopup";
import { TagsSelectPopup } from "@src/components/deprecated/TagsSelectPopup/TagsSelectPopup";
import { getRoutineActionsLabels, getRoutineActionsValues } from "@utils/actionUtilities";
import { Tag } from "data-hooks/company/tags/tags.type";
import { deleteField } from "firebase/firestore";
import { t } from "i18next";
import { DataObjectFieldInput } from "../../../../../components/deprecated/DataObjectFieldInput/DataObjectFieldInput";
import { IconNames } from "../../../../../components/deprecated/Icon/IconNames";
import * as PopUp from "../../../../../components/deprecated/Popup/deprecated_PopUps";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { PropertySelectorPopUp } from "../../../../../components/deprecated/PropertySelectPopUp/PropertySelectPopUp";
import { UserSelectPopup } from "../../../../../components/deprecated/UserSelectPopup/UserSelectPopUp";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { getAllContractorGroups } from "../../../../../utils/hooks/useContractorGroups";
import { DeviationPriority } from "../../../Deviation/DeviationPriority";
import { PropertyData } from "../../../Property/PropertyData";
import { RoutineData } from "../../../Routines/RoutineData";
import { DataObjectAction } from "../../DataObjects/DataObjectHelper";
import { DataObjectHelpers } from "../../DataObjects/DataObjectHelpers";
import { LocalData } from "../../LocalData";
import { RoutineDataHelper } from "./RoutineDataHelper";
import { RoutineDataUtilities } from "./RoutineDataUtilities";

export const getRoutineActionsForData = (
  helper: RoutineDataHelper,
  data: RoutineData,
  selectedDatas: RoutineData[] = []
): DataObjectAction<RoutineData>[] => {
  const isMultipleDataSelected = selectedDatas.length > 1;

  // Prepare values
  const {
    title: titleValue,
    description: descriptionValue,
    priority: priortyValue,
    tags: tagValue,
    properties: propertiesValue,
    contractorGroups: contractorGroupsValue,
    createDaysBefore,
  } = getRoutineActionsValues(data, isMultipleDataSelected);

  // Prepare labels
  const {
    title: titleLabel,
    description: descriptionLabel,
    priority: priorityLabel,
    tags: tagLabel,
    properties: propertiesLabel,
    contractorGroups: contractorGroupsLabel,
  } = getRoutineActionsLabels(isMultipleDataSelected);

  const actions: DataObjectAction<RoutineData>[] = [];
  if (data.hasWriteAccess()) {
    actions.push({
      id: "setCompanies",
      iconName: IconNames.COMPANY,
      title: t("main.routineActions.setCompanies"),
      requiredConsent: (routines) => {
        const companies = LocalData.currentUser.companies;
        return new Promise((resolve) => {
          PopupTemplates.presentMultiSelectInput(
            t("main.selectCompanies"),
            t("main.selectCompanies"),
            companies.map((c) => ({ key: c.id, title: c.name })),
            (ids) => {
              resolve({ dataObjects: routines, data: ids });
            }
          );
        });
      },
      performAction: (companies) => {
        return ToastUtilities.presentSavingToast(data.saveUpdate({ companies }));
      },
    });

    actions.push({
      id: "setProperties",
      iconName: IconNames.PROPERTY,
      title: t("main.routineActions.setProperties"),
      requiredConsent: (routines) => {
        const properties = LocalData.currentUser.properties;
        return new Promise((resolve) => {
          Popup.present((id) => (
            <PropertySelectorPopUp
              title={t("main.routineActions.setProperties")}
              properties={properties}
              defaultSelectedProperties={propertiesValue}
              isMulti={true}
              id={id}
              chosenSelected={(properties) => resolve({ dataObjects: routines, data: properties })}
              label={propertiesLabel}
            />
          ));
        });
      },
      performAction: (selectedProperties: PropertyData[]) => {
        const selectedPropIds = selectedProperties.map(({ id }) => id);
        const existedPropIds = data.properties.map(({ data }) => data.id);

        const combinedPropIds = [...new Set([...selectedPropIds, ...existedPropIds])];

        // going through combined properties ids to determine whether props should be added or removed from routine
        const propertiesToSave = combinedPropIds.reduce<{ [key: string]: {} }>((acc, id) => {
          const isInSelected = selectedPropIds.includes(id);
          const isInExisted = existedPropIds.includes(id);

          const isSelectedButNotExisted = isInSelected && !isInExisted;
          const isExistedButNotSelected = !isInSelected && isInExisted;

          if (isSelectedButNotExisted) {
            acc[id] = {};
          } else if (isExistedButNotSelected) {
            // if existed property wasn't selected then it will be removed
            acc[id] = deleteField();
          }
          return acc;
        }, {});

        if (Object.keys(propertiesToSave).length === 0) {
          return Promise.resolve();
        }

        return ToastUtilities.presentSavingToast(data.saveWrite({ propertyIDs: propertiesToSave }));
      },
    });

    actions.push({
      id: "removePropertyFromRoutine",
      iconName: "remove",
      title: t("main.routineActions.removeProperties"),
      requiredConsent: (routines) => {
        const properties = RoutineDataUtilities.getAllProperties(routines);
        return new Promise((resolve) => {
          Popup.present((id) => (
            <PropertySelectorPopUp
              title={t("main.routineActions.removePropertiesDescription")}
              properties={properties}
              isMulti={true}
              id={id}
              chosenSelected={(propertiesToRemoveFromRoutine) => resolve({ dataObjects: routines, data: propertiesToRemoveFromRoutine })}
            />
          ));
        });
      },
      performAction: (propertiesToRemoveFromRoutines) => {
        return ToastUtilities.presentPromise(t("main.saving"), data.removeProperties(propertiesToRemoveFromRoutines));
      },
    });
    actions.push({
      id: "editTitle",
      iconName: IconNames.PEN,
      title: t("main.editTitle"),
      requiredConsent: (routines) => {
        return new Promise((resolve) =>
          PopupTemplates.presentTextInput({
            title: t("main.editTitle"),
            placeholder: titleValue,
            onConfirm: (id, name) => {
              resolve({ dataObjects: routines, data: name });
              Popup.remove(id);
            },
            defaultValue: titleValue,
            label: titleLabel,
            dataCy: "routine-title-input",
          })
        );
      },
      performAction: (title) => {
        return ToastUtilities.presentSavingToast(data.saveUpdate({ title }));
      },
    });

    actions.push({
      id: "editDescription",
      iconName: IconNames.PEN,
      title: t("main.routineActions.editDescription"),
      requiredConsent: (routines) => {
        return new Promise((resolve) =>
          PopupTemplates.presentTextInput({
            title: t("main.routineActions.editDescription"),
            placeholder: descriptionValue,
            onConfirm: (id, name) => {
              resolve({ dataObjects: routines, data: name });
              Popup.remove(id);
            },
            defaultValue: descriptionValue,
            label: descriptionLabel,
            dataCy: "routine-description-input",
          })
        );
      },
      performAction: (description) => {
        return ToastUtilities.presentSavingToast(data.saveUpdate({ description }));
      },
    });

    actions.push({
      id: "changeContractors",
      iconName: IconNames.CONTRACTORS,
      title: t("main.routineActions.setContractors"),
      requiredConsent: (routines) => {
        return new Promise((resolve) => {
          PopupTemplates.presentMultiSelectInput(
            t("main.routineActions.setContractors"),
            t("main.routineActions.setContractors"),
            getAllContractorGroups().then((contractors) => contractors.map((c) => ({ key: c.id, title: c.name }))),
            (ids) => {
              resolve({ dataObjects: routines, data: ids });
            },
            contractorGroupsValue,
            contractorGroupsLabel
          );
        });
      },
      performAction: (contractors) => {
        return ToastUtilities.presentSavingToast(data.saveUpdate({ contractorGroups: contractors }));
      },
    });

    actions.push({
      id: "changeResponsibles",
      iconName: IconNames.USERS,
      title: t("main.changeResponsibles"),
      requiredConsent: (routines) => {
        const properties = RoutineDataUtilities.getAllProperties(routines);
        return RoutineDataUtilities.getUsersThatAreMemberOfAllProperties(properties).then((users) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <UserSelectPopup id={id} didSelectUsers={(users) => resolve({ dataObjects: routines, data: { all: users } })} options={users} />
            ));
          });
        });
      },
      performAction: (users) => {
        return ToastUtilities.presentSavingToast(data.setResponsibles("all", users));
      },
    });

    actions.push({
      id: "changeExecutors",
      iconName: IconNames.USERS,
      title: t("main.changeExecutors"),
      requiredConsent: (routines) => {
        const properties = RoutineDataUtilities.getAllProperties(routines);
        return RoutineDataUtilities.getUsersThatAreMemberOfAllProperties(properties).then((users) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <UserSelectPopup id={id} didSelectUsers={(users) => resolve({ dataObjects: routines, data: { all: users } })} options={users} />
            ));
          });
        });
      },
      performAction: (users) => {
        return ToastUtilities.presentSavingToast(data.setExecutors("all", users));
      },
    });

    actions.push({
      id: "setTag",
      iconName: IconNames.TAG,
      title: t("main.setTag"),
      requiredConsent: (routines) => {
        const propertyIds = propertiesValue.map((property) => property.id);
        return new Promise((resolve) => {
          Popup.present((id) => (
            <TagsSelectPopup
              id={id}
              title={t("main.editTags")}
              actionTitle={t("main.save")}
              propertyId={propertyIds}
              value={tagValue ?? []}
              onChange={(tags) => resolve({ dataObjects: routines, data: tags })}
            />
          ));
        });
      },
      performAction: (tags) => {
        return ToastUtilities.presentSavingToast(data.saveUpdate({ tags: tags.map((tag: Tag) => tag.id) }));
      },
    });

    actions.push({
      id: "setPriority",
      iconName: IconNames.PRIORITY,
      title: t("main.setPriority"),
      requiredConsent: (routines) => {
        return new Promise((resolve) => {
          Popup.present((id) => (
            <DataObjectFieldInput
              id={id}
              field={DataObjectHelpers.routine.priority}
              onFinish={(value) => resolve({ dataObjects: routines, data: value })}
              context={{ menuPortalTarget: "popup" }}
              defaultValue={priortyValue}
              description={priorityLabel}
            />
          ));
        });
      },
      performAction: (priority: DeviationPriority) => {
        return ToastUtilities.presentSavingToast(data.saveUpdate({ priority: priority.id }));
      },
    });

    actions.push({
      id: "setCreateDaysBefore",
      iconName: IconNames.PEN,
      title: t("routine.actions.createDaysBefore.title"),
      requiredConsent: (routines) => {
        return new Promise((resolve) => {
          Popup.present((id) => (
            <DataObjectFieldInput
              id={id}
              field={DataObjectHelpers.routine.createDaysBefore}
              onFinish={(value) => resolve({ dataObjects: routines, data: value })}
              context={{ menuPortalTarget: "popup" }}
              defaultValue={createDaysBefore}
              description={t("routine.actions.createDaysBefore.description")}
            />
          ));
        });
      },
      performAction: (createDaysBefore: number) => {
        return ToastUtilities.presentSavingToast(data.saveUpdate({ createDaysBefore }));
      },
    });

    actions.push({
      id: "deleteTasks",
      iconName: IconNames.DELETE,
      title: t("main.deleteRoutineTasksInTimeFrame"),
      requiredConsent: (dataObjects) => {
        return new Promise((resolve) => {
          Popup.present((id) => (
            <SelectDatePopUp
              id={id}
              continue={(sDate, eDate) => {
                PopUp.removePopUp();
                resolve({ dataObjects: dataObjects, data: { start: sDate, end: eDate } });
              }}
            />
          ));
        });
      },
      performAction: (dates) => {
        return ToastUtilities.presentSavingToast(data.deleteActivitiesInBulk(dates?.start ?? undefined, dates?.end ?? undefined, undefined));
      },
    });
    actions.push({
      id: data.active ? "make_inactive" : "make_active",
      iconName: data.active ? "pause" : "start",
      title: data.active ? t("main.setAsInactive") : t("main.setAsActive"),
      requiredConsent: (dataObjects) => {
        return new Promise((resolve) => {
          PopupTemplates.presentVerifyAction(data.active ? t("main.verifyMakeInactive") : t("main.verifyMakeActive"), "", () => {
            resolve({ dataObjects });
          });
        });
      },
      performAction: () => {
        return ToastUtilities.presentPromise(t("main.saving"), data.saveUpdate({ active: !data.active }));
      },
    });
    actions.push({
      id: "duplicate",
      iconName: "copy",
      title: t("main.duplicateRoutine"),
      performActionOnDataObjects: (dataObjects) => {
        return new Promise((resolve) => {
          Popup.present((id) => (
            <RoutineDuplicationPopup
              id={id}
              routines={dataObjects}
              didDuplicate={() => {
                resolve({ dataObjects });
              }}
            />
          ));
        });
      },
    });

    if (!data.archived) {
      actions.push({
        id: "archive",
        iconName: "archive",
        title: t("main.archiveRoutine"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve, reject) => {
            if (dataObjects.length > 1) {
              PopupTemplates.presentVerifyAction(t("main.archiveRoutines") + "?", t("main.routinesArchivationCaution"), () => {
                resolve({ dataObjects });
              });
            } else {
              PopupTemplates.presentVerifyAction(t("main.archiveRoutine") + "?", t("main.routineArchivationCaution"), () => {
                resolve({ dataObjects });
              });
            }
          });
        },
        performAction: () => {
          return ToastUtilities.presentSavingToast(data.archive());
        },
      });
    } else {
      actions.push({
        id: "unarchive",
        iconName: "archive",
        title: t("main.unarchiveRoutine"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve, reject) => {
            if (dataObjects.length > 1) {
              PopupTemplates.presentVerifyAction(t("main.unarchiveRoutines") + "?", t("main.routinesDearchivationCaution"), () => {
                resolve({ dataObjects });
              });
            } else {
              PopupTemplates.presentVerifyAction(t("main.unarchiveRoutine") + "?", t("main.routineDearchivationCaution"), () => {
                resolve({ dataObjects });
              });
            }
          });
        },
        performAction: () => {
          return ToastUtilities.presentSavingToast(data.archive());
        },
      });
    }

    actions.push({
      id: "delete",
      iconName: IconNames.DELETE,
      title: t("main.delete") + " " + t("main.routine").toLowerCase(),
      requiredConsent: (dataObjects) => {
        return new Promise((resolve) => {
          PopupTemplates.presentVerifyDeleteDatas(
            helper,
            dataObjects.map((data) => data.title),
            () => {
              resolve({ dataObjects });
            },
            t("main.routineDeletionCaution")
          );
        });
      },
      performAction: () => {
        return ToastUtilities.presentPromise(t("main.deleting"), data.delete());
      },
    });
  }

  return actions;
};
